import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, catchError, filter, map, Observable, switchMap, take, tap, throwError } from 'rxjs';
import { TokenInfo } from '../models/token-info.model';
import { environment } from '../../../environments/environment';
import { AuthResponse, OTPResponse } from '../models/user-login.model';
import { jwtDecode } from "jwt-decode";
import { ChangePasswordResponse } from '../models/change-password.model';
import { NgxPermissionsService } from 'ngx-permissions';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _accessToken: string = '';
  private _accessTokenExp: number = 0;

  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  private readonly _tokenInfo = signal<TokenInfo | null>(null);
  private readonly _isLoggedIn = signal<boolean>(false);

  private loggedUserId: string = '';

  private readonly otp = signal<string>('');
  private readonly otpInfo = signal<OTPResponse | null>(null);

  constructor(private readonly http: HttpClient, private readonly permissionsSrv: NgxPermissionsService) {
    // TODO: Remove
    const cacheAT = sessionStorage.getItem('access_token');
    if (cacheAT) {
      this._accessToken = JSON.parse(cacheAT);
    }
  }

  getAccessToken() {
    return this._accessToken;
  }

  getAccessTokenExp() {
    return this._accessTokenExp;
  }

  isLoggedIn() {
    return this._isLoggedIn.asReadonly();
  }

  tokenInfo() {
    return this._tokenInfo.asReadonly();
  }

  getUserId() {
    return this.loggedUserId;
  }

  setOtp(otp: string) {
    this.otp.set(otp);
  }

  setOtpInfo(otpInfo: OTPResponse) {
    this.otpInfo.set(otpInfo);
  }

  getOtpInfo() {
    return this.otpInfo.asReadonly();
  }

  // TODO: can switch to NgxRolesService
  private setPermissions(permissions: string[]) {
    this.permissionsSrv.loadPermissions(permissions);
  }

  private setToken(newToken: string) {
    this._accessToken = newToken;
    this._isLoggedIn.set(true);
    sessionStorage.setItem('access_token', JSON.stringify(this._accessToken));
    this.setTokenInfo(newToken);
  }

  setTokenInfo(jwtToken: string) {
    const decoded: TokenInfo = jwtDecode(jwtToken);
    this._isLoggedIn.set(true);
    this._accessTokenExp = (decoded.exp);
    this._tokenInfo.set(decoded);
    this.loggedUserId = decoded.user_id;
    this.setPermissions([decoded.role]);
  }

  finalizeLoginWithOtp(): Observable<any> {
    const code = this.otp();
    const sessionId = this.otpInfo()?.sessionId || '';

    return this.http.post<AuthResponse>(
      `${environment.apiGatewayUrl}/api/v1/auth/otp/finalize`,
      { sessionId, code },
      { withCredentials: true } // 🔸 necessario per ricevere cookie HttpOnly
    )
      .pipe(
        tap(result => this.setToken(result.idToken)) // access token (non refresh)
      );
  }

  refreshToken(): Observable<string> {
    // Se NON è in corso un refresh → fai il refresh
    if (!this.isRefreshing) {
      this.isRefreshing = true;

      return this.http.post<{ accessToken: string }>(
        `${environment.apiGatewayUrl}/api/v1/auth/refresh`,
        {},
        { withCredentials: true }
      ).pipe(
        // Estrai solo la stringa del token
        map(res => res.accessToken),
        tap(token => {
          this.setToken(token);

          // Notifica tutti quelli in attesa
          this.refreshSubject.next(token);

          this.isRefreshing = false;
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.refreshSubject.next(null); // fallback: sveglia i subscriber
          return throwError(() => err);
        })
      );

    } else {
      // ⏳ C’è già un refresh in corso → aspetta il risultato
      return this.refreshSubject.pipe(
        filter(token => token !== null),
        take(1) // prendi solo la prima emissione
      );
    }
  }

  logout() {
    if (this._accessToken) {
      return this.http.post(`${environment.apiGatewayUrl}/api/v1/auth/logout`, {});
    } else {
      return undefined;
    }
  }

  clearSession() {
    this._accessToken = '';
    this._accessTokenExp = 0;
    this._tokenInfo.set(null);
    this._isLoggedIn.set(false);
    // clear permissions
    this.permissionsSrv.flushPermissions();
    // clear session storage
    sessionStorage.clear();

    console.debug('Session Cleared!');
  }

  changePassword(oldPassword: string, newPassword: string) {
    return this.http.post<ChangePasswordResponse>(`${environment.apiGatewayUrl}/ugc/api/v1/email/change-password`, { oldPassword, newPassword });
  }

  // Public API
  renewPassword(oldPassword: string, newPassword: string) {
    const otpCode = this.otp();
    const sessionId = this.otpInfo()?.sessionId || '';

    return this.http.post<ChangePasswordResponse>(`${environment.apiGatewayUrl}/ugc/api/v1/email/change-password-expired`, { sessionId, code: otpCode, oldPassword, newPassword });
  }
  
  // Public API
  loginWithOTP(email: string, password: string, recaptchaAction: string, recaptchaToken: string): Observable<OTPResponse> {
    return this.http.post<OTPResponse>(`${environment.apiGatewayUrl}/api/v1/auth/login`, { email, password, recaptchaAction, recaptchaToken }).pipe(
      tap(res => {
        this.setOtpInfo(res);
      })
    );
  }

  // Public API
  resetPassword(oobCode: string, tenantId: string, newPassword: string) {
    return this.http.post<ChangePasswordResponse>(`${environment.apiGatewayUrl}/ugc/api/v1/email/execute-reset-password`, { oobCode, newPassword, tenantId });
  }

  // Public API - Verify OOB code sent to the user, useful when the user has not received the email or the code has expired
  verifyOobCode(oobCode: string, tenantId: string, uid: string) {
    return this.http.post<any>(`${environment.apiGatewayUrl}/ugc/api/v1/ip/verify-oob-code`, { oobCode, tenantId, uid });
  }

  // Public API - Resend OOB code to the user, useful when the user has not received the email or the code has expired
  resendOobCodeEmail(tenantId: string, uid: string) {
    return this.http.post<{ status: string, code: number, message: string }>(`${environment.apiGatewayUrl}/ugc/api/v1/email/first-access/request-new-link`, { tenantId, uid });
  }

  // Public API
  sendForgotPasswordEmail(email: string) {
    return this.http.post<OTPResponse>(`${environment.apiGatewayUrl}/ugc/api/v1/email/forgot-password-start`, { email }).pipe(
      tap(res => {
        this.setOtpInfo(res);
      })
    );
  }

  // Public API
  finalizePasswordReset(newPassword: string) {
    const code = this.otp();
    const sessionId = this.otpInfo()?.sessionId || '';

    return this.http.post<any>(`${environment.apiGatewayUrl}/ugc/api/v1/email/forgot-password-finalize`, { sessionId, code, newPassword });
  }

  // Public API
  resendOTP(email: string, expiredOTP: boolean = false) {
    let requestBody: object = { email };

    if (!expiredOTP) {
      const sessionId = this.otpInfo()?.sessionId || '';
      requestBody = { ...requestBody, sessionId };
    }

    return this.http.post<OTPResponse>(`${environment.apiGatewayUrl}/api/v1/auth/otp/resend`, requestBody).pipe(
      tap(res => {
        this.setOtpInfo(res);
      })
    );
  }

  // Public API
  verifyOTPCode(otpCode: string) {
    const sessionId = this.otpInfo()?.sessionId || '';

    return this.http.post<{ valid: boolean, type: 'LOGIN' | 'FORGOT_PASSWORD' }>(
      `${environment.apiGatewayUrl}/api/v1/auth/otp/verify`,
      { sessionId, otpCode }
    );
  }
}
